// Administration → Utilizatori: users with roles and org-chart units (membership + head/șef).
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { useCatalog } from '../../catalog';
import { navigate, useAuth } from '../../store';
import type { Role, User } from '../../types';
import { Confirm, EmptyState, Field, Modal, Switch, fmtDateTime, useToast } from '../../ui';
import { UsersImportModal } from './UsersImport';
import { Icon } from '../../ui/Icon';
import { Loading, PageHead } from './common';
import { Crown, type OrgUnit } from './OrgChart';

const fmtWhen = (iso?: string | null) => fmtDateTime(iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);
const ROLE_LABEL: Record<string, string> = { ADMIN: 'Administrator', REGISTRY: 'Registratură', DEPARTMENT: 'Compartiment', ALOP_INITIATOR: 'ALOP · Compartiment de specialitate', ALOP_ECONOMIC: 'ALOP · Financiar-contabil (responsabil CAB)', ALOP_CFP: 'ALOP · CFP propriu', ALOP_CFP_DELEGAT: 'ALOP · CFP delegat', ALOP_ORDONATOR: 'ALOP · Ordonator de credite', PORTAL_EDITOR: 'Portal · Editor conținut' };
const ROLE_HINT: Record<string, string> = {
  REGISTRY: 'înregistrează și repartizează documente',
  DEPARTMENT: 'primește documentele repartizate compartimentului său',
  ADMIN: 'configurează sistemul: tipuri, atribute, liste, utilizatori',
};

export function UsersPage() { return <UsersTab />; }

function UsersTab() {
  const toast = useToast();
  const { reload, departments } = useCatalog();
  const { user: me } = useAuth();
  const [items, setItems] = useState<User[] | null>(null);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [del, setDel] = useState<User | null>(null);
  const [importing, setImporting] = useState(false);

  const load = () => api.get<User[]>('/api/auth/users').then(setItems).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); }, []);
  const deptName = (id: number) => departments.find(d => d.id === id)?.name || `#${id}`;
  const unitsOf = (u: User): { id: number; name: string; isHead?: boolean }[] => {
    const heads: number[] = (u as any).heads || [];
    return (u.departmentNames as { id: number; name: string; isHead?: boolean }[] | undefined) || u.departments.map(id => ({ id, name: deptName(id), isHead: heads.includes(id) }));
  };

  const remove = async () => {
    if (!del) return;
    try { await api.del('/api/auth/users/' + del.id); toast('Utilizator șters', 'ok'); setDel(null); load(); reload(); }
    catch (e: any) { toast(e.message, 'error'); }
  };

  return (
    <>
      <PageHead title="Utilizatori" text="Conturile de acces și rolurile lor.">
        <button className="btn" onClick={() => navigate('/admin/org')} title="Unitățile, subordonarea și șefii se definesc în organigramă"><Icon name="org" />Organigramă</button>
        <button className="btn" onClick={() => setImporting(true)}><Icon name="upload" />Importă utilizatori</button>
        <button className="btn primary" onClick={() => setEditing('new')}><Icon name="plus" />Utilizator nou</button>
      </PageHead>
      {importing && <UsersImportModal onClose={() => setImporting(false)} onDone={() => { load(); reload(); }} />}
      {!items ? <Loading /> : items.length === 0 ? <EmptyState icon="users" title="Niciun utilizator" /> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Nume</th><th>Utilizator</th><th>E-mail</th><th>Roluri</th><th>Compartimente</th><th>Activ</th><th>Ultima autentificare</th><th /></tr></thead>
            <tbody>
              {items.map(u => (
                <tr key={u.id} onClick={() => setEditing(u)} style={{ opacity: u.active === false ? .6 : 1 }}>
                  <td><b style={{ fontWeight: 500 }}>{u.displayName}</b></td>
                  <td><span className="code">{u.username}</span></td>
                  <td>{u.email || <span className="muted">—</span>}</td>
                  <td><span className="pill-list">{u.roles.map(r => <span key={r} className={'tag ' + (r === 'ADMIN' ? 'adm-tag-accent' : '')}>{ROLE_LABEL[r] || r}</span>)}</span></td>
                  <td className="wrap">{unitsOf(u).length ? unitsOf(u).map((d, i) => <React.Fragment key={d.id}>{i > 0 && ', '}{d.name}{d.isHead && <span className="ut-headmark"> (șef)</span>}</React.Fragment>) : <span className="muted">—</span>}</td>
                  <td>{u.active === false ? <span className="muted">Nu</span> : 'Da'}</td>
                  <td className="muted">{u.lastLogin ? fmtWhen(u.lastLogin) : 'niciodată'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <button className="icon-btn" title="Editează" onClick={() => setEditing(u)}><Icon name="edit" /></button>
                    <button className="icon-btn" title="Șterge" disabled={u.id === me?.id} onClick={() => setDel(u)}><Icon name="trash" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <UserEditor user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); reload(); }} />}
      {del && <Confirm title="Ștergere utilizator" danger yesLabel="Șterge" onNo={() => setDel(null)} onYes={remove}
        text={<>Ștergeți contul <b>{del.displayName}</b> ({del.username})? Documentele create de acest utilizator rămân în sistem. Dacă vreți doar să blocați accesul, dezactivați contul.</>} />}
    </>
  );
}

interface UserForm { username: string; displayName: string; email: string; password: string; active: boolean; roles: Role[]; departments: number[]; heads: number[] }

function UserEditor({ user, onClose, onSaved }: { user: User | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const { departments } = useCatalog();
  const { user: me, refresh } = useAuth();
  const isNew = !user;
  const [f, setF] = useState<UserForm>({ username: user?.username || '', displayName: user?.displayName || '', email: user?.email || '', password: '', active: user?.active !== false, roles: [...(user?.roles || ['DEPARTMENT'])], departments: [...(user?.departments || [])], heads: [...(((user as any)?.heads as number[]) || [])] });
  const [roles, setRoles] = useState<{ code: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<UserForm>) => setF(x => ({ ...x, ...p }));
  useEffect(() => { api.get<{ code: string; name: string }[]>('/api/auth/roles').then(setRoles).catch(() => setRoles(Object.keys(ROLE_LABEL).map(code => ({ code, name: ROLE_LABEL[code] })))); }, []);

  const toggle = <T,>(arr: T[], v: T) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
  const save = async () => {
    if (!f.displayName.trim() || !f.username.trim()) { toast('Completați numele și utilizatorul', 'error'); return; }
    if (isNew && !f.password) { toast('Introduceți o parolă', 'error'); return; }
    if (!f.roles.length) { toast('Alegeți cel puțin un rol', 'error'); return; }
    setSaving(true);
    try {
      const body: any = { username: f.username.trim(), displayName: f.displayName.trim(), email: f.email.trim() || null, active: f.active, roles: f.roles, departments: f.departments, heads: f.heads.filter(h => f.departments.includes(h)) };
      if (f.password) body.password = f.password;
      if (isNew) await api.post('/api/auth/users', body); else await api.put('/api/auth/users/' + user!.id, body);
      toast(isNew ? 'Utilizator creat' : 'Utilizator salvat', 'ok');
      if (!isNew && user!.id === me?.id) refresh().catch(() => {});
      onSaved();
    } catch (e: any) { toast(e.message, 'error'); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Utilizator nou' : `Utilizator: ${user!.displayName}`} icon="user" onClose={onClose}
      footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={saving} onClick={save}>{saving ? 'Se salvează…' : 'Salvează'}</button></>}>
      <div className="form-grid">
        <Field label="Nume afișat" required><input className="input" autoFocus value={f.displayName} onChange={e => set({ displayName: e.target.value })} placeholder="ex. Maria Popescu" /></Field>
        <Field label="Utilizator" required hint={isNew ? 'Folosit la autentificare; nu se poate schimba ulterior' : 'Nu se poate schimba'}><input className="input" value={f.username} readOnly={!isNew} onChange={e => set({ username: e.target.value.trim() })} placeholder="ex. mpopescu" /></Field>
        <Field label="E-mail"><input className="input" type="email" value={f.email} onChange={e => set({ email: e.target.value })} /></Field>
        <Field label="Parolă" required={isNew} hint={isNew ? undefined : 'Lăsați gol pentru a păstra parola actuală'}><input className="input" type="password" autoComplete="new-password" value={f.password} onChange={e => set({ password: e.target.value })} /></Field>
        <Field className="span2"><Switch on={f.active} onChange={v => set({ active: v })} label="Cont activ (se poate autentifica)" /></Field>
        <Field label="Roluri" required>
          <div className="stack" style={{ gap: 8 }}>
            {roles.map(r => (
              <label key={r.code} className="checkbox" style={{ alignItems: 'flex-start' }}>
                <input type="checkbox" checked={f.roles.includes(r.code as Role)} onChange={() => set({ roles: toggle(f.roles, r.code as Role) })} style={{ marginTop: 2 }} />
                <span><b style={{ fontWeight: 500 }}>{ROLE_LABEL[r.code] || r.name}</b><br /><span className="muted small">{ROLE_HINT[r.code] || ''}</span></span>
              </label>
            ))}
          </div>
        </Field>
        <Field label="Unități din organigramă" className="span2" hint={departments.length ? 'Membrul vede documentele repartizate unității; șeful aprobă răspunsurile unității și ale subordonaților ei' : undefined}>
          {departments.length
            ? <UnitPicker units={departments as unknown as OrgUnit[]} members={f.departments} heads={f.heads} onChange={(departments, heads) => set({ departments, heads })} />
            : <p className="muted small">Nu există unități. <a href="#/admin/org">Creați-le în organigramă.</a></p>}
        </Field>
      </div>
    </Modal>
  );
}

/** Org-tree picker: a checkbox per unit (member) and, when checked, a "Șef" switch. */
function UnitPicker({ units, members, heads, onChange }: { units: OrgUnit[]; members: number[]; heads: number[]; onChange: (members: number[], heads: number[]) => void }) {
  const rows = useMemo(() => {
    const ids = new Set(units.map(u => u.id));
    const kids = new Map<number | null, OrgUnit[]>();
    for (const u of units) { const p = u.parentId != null && ids.has(u.parentId) ? u.parentId : null; if (!kids.has(p)) kids.set(p, []); kids.get(p)!.push(u); }
    kids.forEach(l => l.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'ro')));
    const out: { u: OrgUnit; depth: number }[] = []; const seen = new Set<number>();
    const walk = (p: number | null, depth: number) => { for (const u of kids.get(p) || []) { if (seen.has(u.id)) continue; seen.add(u.id); out.push({ u, depth }); walk(u.id, depth + 1); } };
    walk(null, 0);
    return out;
  }, [units]);
  const toggleMember = (id: number) => members.includes(id) ? onChange(members.filter(x => x !== id), heads.filter(x => x !== id)) : onChange([...members, id], heads);
  const toggleHead = (id: number, on: boolean) => onChange(members, on ? [...heads.filter(x => x !== id), id] : heads.filter(x => x !== id));
  return (
    <div className="ut-tree">
      {rows.map(({ u, depth }) => {
        const m = members.includes(u.id), h = m && heads.includes(u.id);
        return (
          <div key={u.id} className="ut-row" style={{ paddingLeft: 10 + depth * 20 }}>
            <label className="checkbox">
              <input type="checkbox" checked={m} onChange={() => toggleMember(u.id)} />
              <span>{u.name} <span className="ut-kind">{u.kind}{!u.active && ' · inactiv'}</span></span>
            </label>
            {m && <span className={'ut-head ' + (h ? 'on' : '')} title="Șeful aprobă documentele unității">{h && <Crown size={12} />}<Switch on={h} onChange={v => toggleHead(u.id, v)} label="Șef" /></span>}
          </div>
        );
      })}
    </div>
  );
}
